import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  GraduationCap, Briefcase, Rocket, TrendingUp, ArrowRight,
  Users, Award, Star, CheckCircle, Sparkles, Building2,
  Target, Zap, BarChart3, Trophy, Lightbulb, Quote, BookOpen,
  ChevronRight
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Swiss premium light system — White + Deep Ruby (matches platform)
const colors = {
  primary: '#B91C1C',
  primaryDark: '#7F1D1D',
  primaryLight: '#FEF2F2',
  text: '#0A0A0A',
  textSecondary: '#525252',
  textTertiary: '#A3A3A3',
  background: '#FAFAF9',
  surface: '#FFFFFF',
  subtle: '#F5F5F5',
  border: '#E5E5E5',
  success: '#059669',
  gold: '#B45309',
  goldSoft: '#FFFBEB'
};

// Component-level brand: EvalentX Academy
const BRAND_NAME = 'EvalentX Academy';

const FutureSkillsLandingPage = () => {
  const [categories, setCategories] = useState([]);
  const [featuredCourses, setFeaturedCourses] = useState([]);
  const [coursesByCategory, setCoursesByCategory] = useState({});
  const [activeCategory, setActiveCategory] = useState(null);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [apiTestimonials, setApiTestimonials] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, featuredRes, coursesRes, testimonialsRes] = await Promise.all([
          axios.get(`${API}/future-skills/categories`),
          axios.get(`${API}/future-skills/featured`),
          axios.get(`${API}/future-skills/courses?limit=50`),
          axios.get(`${API}/future-skills/testimonials`).catch(() => ({ data: { testimonials: [] } }))
        ]);
        setCategories(catRes.data.categories || []);
        setFeaturedCourses(featuredRes.data.courses || []);
        setApiTestimonials(testimonialsRes.data.testimonials || []);
        const grouped = {};
        (coursesRes.data.courses || []).forEach(course => {
          if (!grouped[course.category]) grouped[course.category] = [];
          grouped[course.category].push(course);
        });
        setCoursesByCategory(grouped);
        if (catRes.data.categories?.length > 0) setActiveCategory(catRes.data.categories[0].id);
      } catch (err) {
        console.error('Failed to fetch data:', err);
      }
    };
    fetchData();
  }, []);

  const defaultTestimonials = [
    { name: 'Priya Sharma', role: 'SDE-2 at Google', course: 'AI Internship 2025', text: 'The program completely transformed my career trajectory. The hands-on projects and industry mentorship prepared me for Google\'s rigorous interview process.', package: '45 LPA' },
    { name: 'Rahul Mehta', role: 'ML Engineer at Amazon', course: 'Placement Accelerator', text: 'From struggling with DSA to cracking Amazon — the structured curriculum and mock interviews made all the difference.', package: '38 LPA' },
    { name: 'Ananya Kumar', role: 'DevOps Lead at Microsoft', course: 'Cloud Bootcamp', text: 'The cloud bootcamp gave me real-world experience that no college could. Within 3 months I had multiple offers.', package: '42 LPA' },
    { name: 'Vikram Singh', role: 'Full Stack Dev at Stripe', course: 'Web Development Track', text: 'The mentors here are actual senior engineers from top companies. Their guidance helped me build a portfolio that stood out.', package: '52 LPA' }
  ];
  const testimonials = apiTestimonials.length > 0 ? apiTestimonials : defaultTestimonials;

  useEffect(() => {
    const t = setInterval(() => setCurrentTestimonial(prev => (prev + 1) % testimonials.length), 6000);
    return () => clearInterval(t);
  }, [testimonials.length]);

  const formatPrice = (price) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(price);

  const getCategoryIcon = (id) => ({ summer_internship: Briefcase, placement_accelerator: Rocket, upskilling: TrendingUp }[id] || GraduationCap);

  const impactStats = [
    { value: '10,000+', label: 'Students Placed', icon: Users },
    { value: '95%', label: 'Placement Rate', icon: Trophy },
    { value: '200+', label: 'Hiring Partners', icon: Building2 },
    { value: '4.9/5', label: 'Student Rating', icon: Star }
  ];

  const transformationMetrics = [
    { label: 'Resume Shortlist Rate', before: '12%', after: '65%', icon: BarChart3 },
    { label: 'Interview Conversion', before: '8%', after: '45%', icon: Target },
    { label: 'Average Package', before: '4 LPA', after: '12 LPA', icon: TrendingUp },
    { label: 'Skill Confidence', before: 'Low', after: 'High', icon: Zap }
  ];

  const partnerCompanies = ['Google', 'Microsoft', 'Amazon', 'Meta', 'Flipkart', 'Swiggy', 'Razorpay', 'PhonePe', 'Zerodha', 'Atlassian', 'Stripe', 'Adobe'];

  const features = [
    { icon: Building2, title: 'Industry Partnerships', description: 'Direct mentorship from engineers at Google, Microsoft, Amazon and 50+ companies.', highlight: '50+ Partners' },
    { icon: Award, title: 'Recognized Certifications', description: 'Certificates co-signed by IITs, NITs and top tech companies.', highlight: 'IIT Certified' },
    { icon: Users, title: 'Small Cohorts', description: 'Max 50 students per batch ensures personalized attention and peer learning.', highlight: '1:10 Ratio' },
    { icon: Briefcase, title: 'Placement Support', description: 'Dedicated placement cell with 100+ partner companies across India.', highlight: '100+ Partners' },
    { icon: Lightbulb, title: 'Project-Based Learning', description: 'Build real products used by thousands — not just theoretical exercises.', highlight: 'Live Projects' },
    { icon: Target, title: 'Lifetime Career Support', description: 'Resume reviews, mock interviews, and career guidance for life.', highlight: 'Lifetime Access' }
  ];

  return (
    <div className="min-h-screen" style={{ background: colors.background, color: colors.text }}>
      {/* Sticky Top Bar */}
      <header className="sticky top-0 z-40 border-b" style={{ background: colors.surface, borderColor: colors.border }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <div className="w-7 h-7 flex items-center justify-center" style={{ background: colors.primary, borderRadius: '2px' }}>
              <Sparkles size={14} className="text-white" />
            </div>
            <span className="font-extrabold tracking-tight text-base" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>EvalentX</span>
            <span className="hidden sm:inline-block px-1.5 py-0.5 text-[9px] font-bold tracking-[0.2em] uppercase border" style={{ borderColor: colors.border, color: colors.textTertiary, borderRadius: '1px' }}>Academy</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm font-semibold" style={{ color: colors.textSecondary }}>
            <a href="#programs" className="hover:text-[#B91C1C] transition-colors">Programs</a>
            <a href="#outcomes" className="hover:text-[#B91C1C] transition-colors">Outcomes</a>
            <a href="#mentors" className="hover:text-[#B91C1C] transition-colors">Mentors</a>
            <a href="#stories" className="hover:text-[#B91C1C] transition-colors">Stories</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/academy/courses" className="px-3 py-2 text-xs sm:text-sm font-bold tracking-wide uppercase border transition-colors hover:bg-[#FAFAF9]"
              style={{ borderColor: colors.border, color: colors.text, borderRadius: '2px' }}
              data-testid="academy-browse-courses">
              Browse Courses
            </Link>
            <Link to="/codearena/auth" className="px-3 py-2 text-xs sm:text-sm font-bold tracking-wide uppercase text-white transition-transform hover:-translate-y-0.5"
              style={{ background: colors.primary, borderRadius: '2px' }}
              data-testid="academy-apply">
              Apply Now
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
          <div className="grid lg:grid-cols-[1fr_460px] gap-10 lg:gap-16 items-center">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 border" style={{ borderColor: colors.border, background: colors.surface, borderRadius: '2px' }}>
                <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: colors.success }} />
                <span className="text-[11px] font-bold tracking-[0.2em] uppercase" style={{ color: colors.textSecondary }}>
                  Now Enrolling — Spring 2026 Cohort
                </span>
              </div>

              <h1 className="mt-6 text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[0.95]"
                style={{ fontFamily: "'Space Grotesk', sans-serif", color: colors.text }}>
                EvalentX
                <br />
                <span style={{ color: colors.primary }}>Academy</span>.
              </h1>

              <p className="mt-6 text-base sm:text-lg leading-relaxed max-w-xl" style={{ color: colors.textSecondary }}>
                Career-defining programs co-built with engineers at <strong style={{ color: colors.text }}>Google, Microsoft, Amazon</strong>. Hands-on projects, recruiter-trusted certifications, and lifetime placement support — engineered for the next 10,000 hires.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a href="#programs"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-bold tracking-[0.1em] uppercase text-white transition-transform hover:-translate-y-0.5"
                  style={{ background: colors.primary, borderRadius: '2px' }}
                  data-testid="explore-programs-btn">
                  Explore Programs <ArrowRight size={15} />
                </a>
                <a href="#outcomes"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-bold tracking-[0.1em] uppercase border transition-colors hover:bg-[#FAFAF9]"
                  style={{ borderColor: colors.text, color: colors.text, borderRadius: '2px' }}
                  data-testid="view-outcomes-btn">
                  View Outcomes
                </a>
              </div>

              {/* Inline trust row */}
              <div className="mt-10 pt-6 border-t flex flex-wrap items-center gap-x-6 gap-y-3" style={{ borderColor: colors.border }}>
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-2">
                    {['#FCA5A5', '#FCD34D', '#86EFAC', '#A5F3FC', '#C4B5FD'].map((bg, i) => (
                      <div key={i} className="w-7 h-7 rounded-full border-2 border-white flex items-center justify-center text-[10px] font-bold"
                        style={{ background: bg, color: colors.text }}>
                        {String.fromCharCode(65 + i)}
                      </div>
                    ))}
                  </div>
                  <div>
                    <p className="text-sm font-bold tabular-nums" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>10,000+</p>
                    <p className="text-[10px] font-bold tracking-[0.15em] uppercase" style={{ color: colors.textTertiary }}>Students placed</p>
                  </div>
                </div>
                <div className="h-8 w-px" style={{ background: colors.border }} />
                <div className="flex items-center gap-2">
                  <div className="flex" style={{ color: '#F59E0B' }}>
                    {[1,2,3,4,5].map(i => <Star key={i} size={14} fill="currentColor" strokeWidth={0} />)}
                  </div>
                  <div>
                    <p className="text-sm font-bold tabular-nums" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>4.9/5</p>
                    <p className="text-[10px] font-bold tracking-[0.15em] uppercase" style={{ color: colors.textTertiary }}>2.5k+ reviews</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Hero collage card */}
            <div className="relative hidden lg:block">
              <div className="relative z-10 bg-white border p-7" style={{ borderColor: colors.border, borderRadius: '2px' }}>
                <div className="flex items-center justify-between mb-5">
                  <p className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: colors.textTertiary }}>Spring 2026 Snapshot</p>
                  <span className="px-2 py-0.5 text-[9px] font-bold tracking-[0.15em] uppercase text-white" style={{ background: colors.primary, borderRadius: '1px' }}>Live</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <StatTile icon={Briefcase} label="Internship Tracks" value="6" />
                  <StatTile icon={Rocket} label="Placement Cohorts" value="4" />
                  <StatTile icon={GraduationCap} label="Live Mentors" value="120+" highlight />
                  <StatTile icon={Award} label="Avg. Package" value="₹12 LPA" />
                </div>
                <div className="mt-5 pt-5 border-t" style={{ borderColor: colors.border }}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center" style={{ background: colors.goldSoft, borderRadius: '2px' }}>
                      <Award size={18} style={{ color: colors.gold }} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold tracking-tight" style={{ color: colors.text, fontFamily: "'Space Grotesk', sans-serif" }}>IIT-Certified Curriculum</p>
                      <p className="text-[11px]" style={{ color: colors.textTertiary }}>Co-signed by IITs, NITs and 50+ tech companies</p>
                    </div>
                  </div>
                </div>
              </div>
              {/* Decorative accent (behind the card; doesn't overlap content) */}
              <div className="absolute -top-3 -right-3 w-12 h-12 -z-10" style={{ background: colors.primary, borderRadius: '2px' }} />
              <div className="absolute -bottom-3 -left-3 w-10 h-10 -z-10 border-4" style={{ borderColor: colors.text, background: colors.surface, borderRadius: '2px' }} />
            </div>
          </div>
        </div>
      </section>

      {/* IMPACT STATS STRIP */}
      <section className="border-y" style={{ background: colors.surface, borderColor: colors.border }} id="impact">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 grid grid-cols-2 md:grid-cols-4 gap-3">
          {impactStats.map((s, i) => (
            <div key={i} className="px-2" data-testid={`academy-impact-${i}`}>
              <div className="flex items-center gap-2 mb-1.5">
                <s.icon size={13} style={{ color: colors.primary }} />
                <p className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: colors.textTertiary }}>{s.label}</p>
              </div>
              <p className="text-3xl sm:text-4xl font-extrabold tabular-nums tracking-tighter"
                style={{ fontFamily: "'Space Grotesk', sans-serif", color: colors.text }}>
                {s.value}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* PROGRAMS */}
      <section id="programs" className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
          <div>
            <p className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: colors.primary }}>Programs</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tighter mt-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Choose your career track
            </h2>
            <p className="text-sm mt-2 max-w-xl" style={{ color: colors.textSecondary }}>
              Every track is engineered around verifiable outcomes — placements, packages, projects you can ship.
            </p>
          </div>
          <Link to="/academy/courses" className="text-xs font-bold tracking-[0.1em] uppercase inline-flex items-center gap-1.5 hover:underline" style={{ color: colors.primary }}>
            See all courses <ArrowRight size={13} />
          </Link>
        </div>

        <div className="grid lg:grid-cols-3 gap-3">
          {(categories.length > 0 ? categories : [
            { id: 'summer_internship', name: 'Summer Internships', description: 'Real industry work in 6–8 weeks' },
            { id: 'placement_accelerator', name: 'Placement Accelerator', description: 'Crack tier-1 product companies' },
            { id: 'upskilling', name: 'Upskilling Bootcamps', description: 'Cloud, AI, Web — pick your stack' }
          ]).map((cat, i) => {
            const Icon = getCategoryIcon(cat.id);
            const courseCount = (coursesByCategory[cat.id] || []).length;
            const isActive = activeCategory === cat.id;
            return (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                data-testid={`academy-program-${cat.id}`}
                className="group relative bg-white border p-6 text-left transition-all overflow-hidden"
                style={{
                  borderColor: isActive ? colors.primary : colors.border,
                  borderRadius: '2px',
                  boxShadow: isActive ? '0 12px 32px rgba(185,28,28,0.12)' : 'none'
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = colors.primary; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = isActive ? colors.primary : colors.border; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <div className="absolute top-3 right-3 text-[10px] font-bold tracking-[0.15em] uppercase tabular-nums"
                  style={{ color: colors.textTertiary, fontFamily: "'IBM Plex Mono', monospace" }}>
                  0{i + 1}
                </div>
                <div className="w-11 h-11 flex items-center justify-center" style={{ background: colors.primaryLight, borderRadius: '2px' }}>
                  <Icon size={20} style={{ color: colors.primary }} />
                </div>
                <h3 className="text-xl font-extrabold mt-5 tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif", color: colors.text }}>
                  {cat.name}
                </h3>
                <p className="text-sm leading-relaxed mt-2" style={{ color: colors.textSecondary }}>
                  {cat.description}
                </p>
                <div className="mt-5 pt-5 border-t flex items-center justify-between" style={{ borderColor: colors.border }}>
                  <span className="text-[10px] font-bold tracking-[0.2em] uppercase tabular-nums" style={{ color: colors.textTertiary, fontFamily: "'IBM Plex Mono', monospace" }}>
                    {courseCount} {courseCount === 1 ? 'Course' : 'Courses'}
                  </span>
                  <span className="text-xs font-bold tracking-wide inline-flex items-center gap-1 transition-transform group-hover:translate-x-1" style={{ color: colors.primary }}>
                    Explore <ChevronRight size={13} />
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Active category courses */}
        {activeCategory && (coursesByCategory[activeCategory] || []).length > 0 && (
          <div className="mt-10">
            <p className="text-[10px] font-bold tracking-[0.2em] uppercase mb-3" style={{ color: colors.textTertiary }}>
              Featured in this track
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {(coursesByCategory[activeCategory] || []).slice(0, 6).map(course => (
                <CourseCard key={course.id} course={course} formatPrice={formatPrice} />
              ))}
            </div>
          </div>
        )}
      </section>

      {/* FEATURED COURSES */}
      {featuredCourses.length > 0 && (
        <section className="border-y" style={{ background: colors.surface, borderColor: colors.border }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
              <div>
                <p className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: colors.primary }}>Featured</p>
                <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tighter mt-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  Most-applied programs
                </h2>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {featuredCourses.slice(0, 6).map(course => (
                <CourseCard key={course.id} course={course} formatPrice={formatPrice} featured />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* OUTCOMES — Before / After */}
      <section id="outcomes" className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <p className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: colors.primary }}>Outcomes</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tighter mt-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            The before / after
          </h2>
          <p className="text-sm mt-3" style={{ color: colors.textSecondary }}>
            Cohort medians — measured at admission vs. 90 days post-graduation.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {transformationMetrics.map((m, i) => (
            <div key={i} className="bg-white border p-5" style={{ borderColor: colors.border, borderRadius: '2px' }}
              data-testid={`academy-outcome-${i}`}>
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: colors.textTertiary }}>{m.label}</p>
                <m.icon size={14} style={{ color: colors.textTertiary }} />
              </div>
              <div className="mt-4 grid grid-cols-[1fr_auto_1fr] items-center gap-2">
                <div>
                  <p className="text-[9px] font-bold tracking-[0.2em] uppercase" style={{ color: colors.textTertiary }}>Before</p>
                  <p className="text-2xl font-extrabold tabular-nums tracking-tighter" style={{ color: colors.textTertiary, fontFamily: "'Space Grotesk', sans-serif", textDecoration: 'line-through' }}>
                    {m.before}
                  </p>
                </div>
                <ArrowRight size={16} style={{ color: colors.primary }} />
                <div>
                  <p className="text-[9px] font-bold tracking-[0.2em] uppercase" style={{ color: colors.primary }}>After</p>
                  <p className="text-2xl font-extrabold tabular-nums tracking-tighter" style={{ color: colors.text, fontFamily: "'Space Grotesk', sans-serif" }}>
                    {m.after}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* WHY EVALENTX ACADEMY */}
      <section id="mentors" className="border-y" style={{ background: colors.surface, borderColor: colors.border }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="grid lg:grid-cols-[1fr_auto] items-end gap-3 flex-wrap mb-10">
            <div>
              <p className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: colors.primary }}>Why {BRAND_NAME}</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tighter mt-1 max-w-2xl" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Built differently. Engineered for outcomes.
              </h2>
            </div>
            <p className="text-sm max-w-md lg:text-right" style={{ color: colors.textSecondary }}>
              No fluff. Six pillars our graduates credit for landing tier-1 offers.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {features.map((f, i) => (
              <div key={i} className="border p-5 transition-all" style={{ borderColor: colors.border, background: colors.surface, borderRadius: '2px' }}
                data-testid={`academy-feature-${i}`}
                onMouseEnter={e => { e.currentTarget.style.borderColor = colors.primary; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = colors.border; }}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="w-10 h-10 flex items-center justify-center border" style={{ borderColor: colors.border, background: colors.primaryLight, borderRadius: '2px' }}>
                    <f.icon size={18} style={{ color: colors.primary }} />
                  </div>
                  <span className="px-2 py-0.5 text-[9px] font-bold tracking-[0.15em] uppercase border"
                    style={{ borderColor: colors.text, color: colors.text, borderRadius: '1px' }}>
                    {f.highlight}
                  </span>
                </div>
                <h3 className="text-lg font-extrabold mt-4 tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif", color: colors.text }}>
                  {f.title}
                </h3>
                <p className="text-sm leading-relaxed mt-2" style={{ color: colors.textSecondary }}>
                  {f.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HIRING PARTNERS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="text-center mb-10">
          <p className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: colors.primary }}>Hiring Partners</p>
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tighter mt-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            200+ companies recruit from {BRAND_NAME}
          </h2>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-px overflow-hidden border" style={{ background: colors.border, borderColor: colors.border, borderRadius: '2px' }}>
          {partnerCompanies.map((p, i) => (
            <div key={i} className="bg-white py-6 px-3 flex items-center justify-center transition-colors hover:bg-[#FAFAF9]">
              <span className="text-base font-extrabold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif", color: colors.textSecondary }}>
                {p}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="stories" className="border-y" style={{ background: colors.surface, borderColor: colors.border }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="text-center mb-10">
            <p className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: colors.primary }}>Stories</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tighter mt-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Where our graduates work
            </h2>
          </div>

          <div className="grid lg:grid-cols-[1.5fr_1fr] gap-6">
            {/* Featured testimonial */}
            <div className="border p-7 sm:p-9 relative overflow-hidden" style={{ background: colors.text, color: '#fff', borderColor: colors.text, borderRadius: '2px' }}>
              <Quote size={56} className="absolute top-4 right-4 opacity-10" />
              <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-white/60">Featured</p>
              <p className="text-xl sm:text-2xl font-bold leading-snug mt-3 tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                "{testimonials[currentTestimonial]?.text}"
              </p>
              <div className="mt-6 pt-6 border-t border-white/10 flex items-center gap-3">
                <div className="w-11 h-11 rounded-full flex items-center justify-center text-base font-extrabold" style={{ background: colors.primary, fontFamily: "'Space Grotesk', sans-serif" }}>
                  {testimonials[currentTestimonial]?.name?.charAt(0) || 'A'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold">{testimonials[currentTestimonial]?.name}</p>
                  <p className="text-[11px] text-white/60">{testimonials[currentTestimonial]?.role}</p>
                </div>
                {testimonials[currentTestimonial]?.package && (
                  <span className="px-3 py-1 text-xs font-bold tracking-wide tabular-nums"
                    style={{ background: '#FCD34D', color: '#7C2D12', fontFamily: "'IBM Plex Mono', monospace", borderRadius: '1px' }}>
                    {testimonials[currentTestimonial].package}
                  </span>
                )}
              </div>
              {/* Dots */}
              <div className="mt-6 flex gap-1.5">
                {testimonials.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentTestimonial(i)}
                    aria-label={`Show testimonial ${i + 1}`}
                    className="transition-all"
                    style={{
                      width: i === currentTestimonial ? '28px' : '8px',
                      height: '4px',
                      background: i === currentTestimonial ? '#fff' : 'rgba(255,255,255,0.25)',
                      borderRadius: '1px'
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Side mini cards */}
            <div className="space-y-3">
              {testimonials.slice(0, 3).map((t, i) => (
                <div key={i} className="border p-4 bg-white" style={{ borderColor: colors.border, borderRadius: '2px' }}>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-extrabold" style={{ background: colors.primaryLight, color: colors.primary, fontFamily: "'Space Grotesk', sans-serif" }}>
                      {t.name?.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold truncate" style={{ color: colors.text }}>{t.name}</p>
                      <p className="text-[11px] truncate" style={{ color: colors.textTertiary }}>{t.role}</p>
                    </div>
                    {t.package && (
                      <span className="text-[11px] font-bold tabular-nums shrink-0"
                        style={{ color: colors.primary, fontFamily: "'IBM Plex Mono', monospace" }}>
                        {t.package}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <div className="border p-8 sm:p-12 relative overflow-hidden" style={{ background: colors.text, color: '#fff', borderColor: colors.text, borderRadius: '2px' }}>
          {/* Diagonal ruby slab */}
          <div className="absolute top-0 right-0 bottom-0 w-1/3" style={{ background: colors.primary, clipPath: 'polygon(30% 0, 100% 0, 100% 100%, 0% 100%)' }} />

          <div className="relative grid lg:grid-cols-[1.5fr_auto] items-center gap-6">
            <div>
              <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-white/60">Spring 2026</p>
              <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tighter mt-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Become tier-1 hire-ready in 90 days.
              </h2>
              <p className="text-sm sm:text-base mt-4 max-w-xl text-white/70">
                Limited cohort. 1:10 mentor ratio. Lifetime placement support.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/academy/courses"
                  className="inline-flex items-center gap-2 px-6 py-3 text-sm font-bold tracking-[0.1em] uppercase text-white transition-transform hover:-translate-y-0.5"
                  style={{ background: colors.primary, borderRadius: '2px' }}
                  data-testid="academy-cta-browse">
                  Browse all courses <ArrowRight size={14} />
                </Link>
                <Link to="/codearena/auth"
                  className="inline-flex items-center gap-2 px-6 py-3 text-sm font-bold tracking-[0.1em] uppercase border border-white/20 text-white hover:bg-white/10 transition-colors"
                  style={{ borderRadius: '2px' }}
                  data-testid="academy-cta-apply">
                  Talk to admissions
                </Link>
              </div>
            </div>
            <div className="hidden lg:flex flex-col items-end gap-2 relative z-10">
              <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-white/60">Cohort starts in</p>
              <p className="text-6xl font-extrabold tabular-nums tracking-tighter text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                14
              </p>
              <p className="text-sm font-bold tracking-[0.1em] uppercase text-white/80">Days</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t" style={{ background: colors.surface, borderColor: colors.border }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 flex items-center justify-center" style={{ background: colors.primary, borderRadius: '2px' }}>
              <Sparkles size={11} className="text-white" />
            </div>
            <span className="font-extrabold text-sm tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>EvalentX</span>
            <span className="px-1.5 py-0.5 text-[9px] font-bold tracking-[0.2em] uppercase border" style={{ borderColor: colors.border, color: colors.textTertiary, borderRadius: '1px' }}>Academy</span>
          </div>
          <p className="text-[11px]" style={{ color: colors.textTertiary }}>
            © 2026 EvalentX · Spring cohort enrolling · careers@evalentx.com
          </p>
        </div>
      </footer>
    </div>
  );
};

const StatTile = ({ icon: Icon, label, value, highlight }) => (
  <div className="border p-3" style={{
    borderColor: highlight ? colors.primary : colors.border,
    background: highlight ? colors.primaryLight : colors.surface,
    borderRadius: '2px'
  }}>
    <div className="flex items-center gap-1.5 mb-1">
      <Icon size={12} style={{ color: highlight ? colors.primary : colors.textTertiary }} />
      <p className="text-[9px] font-bold tracking-[0.15em] uppercase" style={{ color: highlight ? colors.primary : colors.textTertiary }}>{label}</p>
    </div>
    <p className="text-xl font-extrabold tabular-nums tracking-tighter" style={{ color: colors.text, fontFamily: "'Space Grotesk', sans-serif" }}>
      {value}
    </p>
  </div>
);

const CourseCard = ({ course, formatPrice, featured = false }) => (
  <Link
    to={`/academy/course/${course.slug}`}
    className="group block border p-5 transition-all bg-white"
    style={{ borderColor: colors.border, borderRadius: '2px' }}
    onMouseEnter={e => { e.currentTarget.style.borderColor = colors.primary; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(185,28,28,0.08)'; }}
    onMouseLeave={e => { e.currentTarget.style.borderColor = colors.border; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
    data-testid={`academy-course-${course.slug}`}
  >
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-1.5 text-[10px] font-bold tracking-[0.15em] uppercase"
        style={{ color: colors.textTertiary, fontFamily: "'IBM Plex Mono', monospace" }}>
        <BookOpen size={11} /> {course.duration_weeks ? `${course.duration_weeks}W` : 'Flexible'}
      </div>
      {featured && (
        <span className="px-2 py-0.5 text-[9px] font-bold tracking-[0.15em] uppercase text-white" style={{ background: colors.primary, borderRadius: '1px' }}>
          Featured
        </span>
      )}
    </div>
    <h3 className="text-base font-extrabold mt-3 tracking-tight leading-snug" style={{ fontFamily: "'Space Grotesk', sans-serif", color: colors.text }}>
      {course.title}
    </h3>
    {course.short_description && (
      <p className="text-xs leading-relaxed mt-2 line-clamp-2" style={{ color: colors.textSecondary }}>
        {course.short_description}
      </p>
    )}
    <div className="mt-4 pt-4 border-t flex items-center justify-between" style={{ borderColor: colors.border }}>
      <p className="text-base font-extrabold tabular-nums" style={{ color: colors.text, fontFamily: "'IBM Plex Mono', monospace" }}>
        {course.price > 0 ? formatPrice(course.price) : 'Free'}
      </p>
      <span className="text-[11px] font-bold tracking-wide inline-flex items-center gap-1 transition-transform group-hover:translate-x-1" style={{ color: colors.primary }}>
        Apply <ChevronRight size={12} />
      </span>
    </div>
  </Link>
);

export default FutureSkillsLandingPage;
